import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  Facebook,
  Instagram,
  Check,
  ArrowRight,
  Video,
  ShoppingCart,
  Palette,
  TrendingUp,
  Globe,
  Sparkles,
  Users,
  Mail,
  Briefcase,
  GraduationCap,
  Store,
  PenTool,
  MessageSquare,
  Wrench,
  CheckCircle,
} from "lucide-react";
import { FOOTER_SOCIAL_LINKS } from "@/components/ui/SocialLinks";
import { HeroNav } from "@/components/ui/hero/HeroNav";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { HeroSocialProof } from "@/components/ui/hero/HeroSocialProof";
import { Footer } from "@/components/ui/footer";
import { ContactSection } from "@/components/sections/ContactSection";
import { AboutSection } from "@/components/sections/AboutSection";
import { WhyUsSection } from "@/components/sections/WhyUsSection";
import type { GrowthItem } from "@/components/sections/WhyUsSection.data";
import { cn } from "@/lib/utils";
import { scrollToAnchor } from "@/lib/scroll-to-anchor";
import logo from "@/assets/mind-media-logo.svg";

export const Route = createFileRoute("/1-on-1-website-help")({
  head: () => ({
    meta: [
      { name: "robots", content: "noindex, nofollow" },
      { title: "1-on-1 Website Help — Mind Media" },
      {
        name: "description",
        content:
          "Live screen-sharing sessions with a senior creative director. Two hours of hands-on help plus 15 days of text support.",
      },
      { property: "og:title", content: "1-on-1 Website Help — Mind Media" },
      {
        property: "og:description",
        content:
          "Stop wrestling with your website on your own. Live, hands-on help from a real web designer.",
      },
    ],
  }),
  component: OneOnOnePage,
});

const gradText =
  "bg-gradient-to-r from-brand-blue via-purple-500 to-pink-500 bg-clip-text text-transparent";

const oneOnOneGrowthItems: GrowthItem[] = [
  {
    title: "Fix what's actually stuck",
    description:
      "We troubleshoot your real site, plugin, or layout problem — no generic tutorials.",
    Icon: Wrench,
    bgClass: "bg-[#3b82f6]/10",
    textClass: "text-[#3b82f6]",
  },
  {
    title: "Learn by doing",
    description: "You build alongside a pro so the same issue never stops you again.",
    Icon: GraduationCap,
    bgClass: "bg-[#a855f7]/10",
    textClass: "text-[#a855f7]",
  },
  {
    title: "Walk away clear",
    description: "Leave with working fixes, clear next steps, and 15 days of text backup.",
    Icon: CheckCircle,
    bgClass: "bg-[#ec4899]/10",
    textClass: "text-[#ec4899]",
  },
];

function OneOnOnePage() {
  const navigate = useNavigate();
  const goToMockup = () => navigate({ to: "/get-mockup" });
  return (
    <main className="min-h-screen bg-background text-foreground">
      {/* HERO */}
      <section className="relative isolate overflow-hidden bg-background pb-4 text-center">
        <div
          aria-hidden
          className="pointer-events-none absolute -left-40 -bottom-40 sm:-left-56 sm:-bottom-56 h-[420px] w-[420px] sm:h-[720px] sm:w-[720px] rounded-full bg-hero-glow-warm opacity-35 blur-3xl sm:blur-[120px]"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute -right-40 -top-40 sm:-right-56 sm:-top-56 h-[420px] w-[420px] sm:h-[720px] sm:w-[720px] rounded-full bg-hero-glow-cool opacity-40 blur-3xl sm:blur-[120px]"
        />
        <HeroNav
          logo={<img src={logo} alt="Mind Media" className="h-7 w-auto" />}
          navigation={[
            { label: "Work", href: "/#work" },
            { label: "About", href: "#about" },
            { label: "Services", href: "/#pricing" },
            { label: "Pricing", href: "#pricing" },
            { label: "FAQ", href: "#faq" },
            { label: "Contact", href: "#contact" },
          ]}
          ctaButton={{ label: "Book a Meeting", href: "#contact" }}
          mobilePricingHref="#pricing"
        />
        <div className="relative mx-auto px-6 mt-12 max-w-4xl -translate-y-5 text-left sm:text-center sm:mt-16 lg:max-w-none">
          <HeroSocialProof mobileAlign="left" />
          <h1 className="mx-auto max-w-3xl text-left text-4xl sm:text-center font-bold tracking-tight text-foreground sm:text-5xl lg:max-w-none lg:whitespace-nowrap lg:text-5xl">
            Stop wrestling with your website <span className={gradText}>on your own.</span>
          </h1>
          <p className="mx-auto mt-5 max-w-3xl text-lg text-muted-foreground sm:text-xl">
            Book a live screen-share with a senior creative director who builds sites for a living.
            Bring your half-finished pages, your broken plugins, your "why won't this work"
            questions. Walk away knowing exactly what to do.
          </p>
          <div className="mt-8 flex flex-wrap justify-start gap-3 sm:justify-center">
            <a
              href="#contact"
              onClick={(e) => {
                e.preventDefault();
                scrollToAnchor("contact");
              }}
              className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-brand-blue to-[#ec4899] px-6 py-3 text-sm font-bold text-white shadow-lg shadow-brand-blue/40 transition hover:-translate-y-0.5 hover:opacity-90"
            >
              Book my session <ArrowRight className="h-4 w-4" />
            </a>
            <a
              href="#pricing"
              onClick={(e) => {
                e.preventDefault();
                scrollToAnchor("pricing");
              }}
              className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-6 py-3 text-sm font-bold text-foreground transition hover:-translate-y-0.5 hover:bg-accent"
            >
              Pricing
            </a>
          </div>
          <div className="relative mx-auto mt-8 flex flex-wrap justify-start gap-x-16 sm:justify-center gap-y-4">
            {[
              "A real designer, not a tutorial",
              "Your screen, your project",
              "Text support after",
            ].map((p) => (
              <span key={p} className="inline-flex items-center gap-2 text-base font-medium">
                <span className="flex h-5 w-5 items-center justify-center rounded-full border border-brand-green bg-white">
                  <Check className="h-3 w-3 text-brand-green" strokeWidth={3} />
                </span>
                {p}
              </span>
            ))}
          </div>
        </div>
        <div className="relative">
          <WhyUsSection
            onCtaClick={goToMockup}
            variant="hero"
            showTrustBar={false}
            showRealExperience={false}
            withDivider={false}
            shareloId="Zfc79RMdg6HUzt41bgJVH"
            shareloTitle="1-on-1 website help — customer story"
            headline={
              <>
                <span className="block sm:inline">What you get</span>{" "}
                <span className="block sm:inline">
                  <span className="inline-flex items-baseline whitespace-nowrap sm:inline">
                    with our{"\u00A0"}
                    <span className="text-gradient-cycle align-baseline">live session</span>
                  </span>
                </span>
              </>
            }
            growthItems={oneOnOneGrowthItems}
          />
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="bg-muted/30 px-6 py-20">
        <div className="mx-auto max-w-6xl">
          <SectionHead
            maxWidth="max-w-3xl lg:max-w-none"
            titleClassName="lg:whitespace-nowrap"
            kickerWeight="font-bold"
            kicker="How it works"
            title={
              <>
                Your screen, your questions, <span className={gradText}>a real expert.</span>
              </>
            }
          >
            No pre-recorded courses. No generic advice. Live, hands-on help on your actual website.
          </SectionHead>
          <div className="grid gap-6 md:grid-cols-3">
            {[
              {
                n: 1,
                h: "Book your session",
                p: "Pick a date and time that works. We'll send a meeting link and a short questionnaire so we hit the ground running.",
              },
              {
                n: 2,
                h: "Jump on a video call",
                p: "Share your screen and walk us through your site. We troubleshoot, build pages with you, and answer every question you've got.",
              },
              {
                n: 3,
                h: "Text us for 15 days",
                p: "After the call you get 15 days of text support. Stuck on something we covered? Message us and we'll get you through it.",
              },
            ].map((s) => (
              <div
                key={s.n}
                className="flex items-start gap-5 rounded-2xl border border-border bg-card p-6"
              >
                <div
                  className={cn(
                    "flex-shrink-0 text-6xl leading-none tracking-tight font-bold text-brand-blue",
                  )}
                >
                  {s.n}
                </div>
                <div>
                  <h3 className="text-base font-bold tracking-tight">{s.h}</h3>
                  <p className="mt-2 text-sm text-muted-foreground">{s.p}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT'S INCLUDED */}
      <section id="included" className="px-6 py-20">
        <div className="mx-auto max-w-6xl">
          <div className="grid items-center gap-12 lg:grid-cols-[1.5fr_1fr] lg:gap-10">
            <div className="text-center lg:text-left">
              <Kicker className="font-bold">The offer</Kicker>
              <h2 className="mt-4 text-4xl font-bold tracking-tight text-foreground sm:text-5xl lg:text-[1.95rem] lg:leading-[1.1] xl:text-[2.15rem] 2xl:text-[2.35rem]">
                Two hours, plus <span className={gradText}>15 days of support.</span>
              </h2>
              <p className="mt-4 text-lg leading-relaxed text-muted-foreground">
                Two full hours of live screen-sharing on a video call, plus 15 days of follow-up
                text support. No subscriptions, no contracts, no surprises.
              </p>
            </div>
            <div className="mx-auto w-full max-w-md rounded-3xl border border-border bg-card p-8 shadow-lg lg:mx-0 lg:max-w-none">
              <h3 className="text-xl font-bold tracking-tight">What's included</h3>
              <ul className="mt-5 space-y-3">
                {[
                  "Live screen-share with a senior creative director",
                  "We build and fix things with you in real time",
                  "A short prep questionnaire so no time is wasted",
                  "15 days of text support after the call",
                ].map((li) => (
                  <li key={li} className="flex items-start gap-3 text-[15px] font-medium">
                    <Check
                      className="mt-0.5 h-5 w-5 flex-shrink-0 text-brand-green"
                      strokeWidth={3}
                    />
                    {li}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT WE COVER */}
      <section id="cover" className="bg-muted/30 px-6 py-20">
        <div className="mx-auto max-w-6xl">
          <SectionHead
            maxWidth="max-w-4xl lg:max-w-none"
            titleClassName="lg:whitespace-nowrap"
            kickerWeight="font-bold"
            kicker="What we cover"
            title={
              <>
                Whatever your site needs,{" "}
                <span className={gradText}>we'll tackle it together.</span>
              </>
            }
          >
            Every session is different because every website is different. These are the things
            people bring most.
          </SectionHead>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {[
              {
                icon: Globe,
                h: "WordPress & Elementor",
                p: "Page building, theme setup, plugins, Elementor widgets, templates, and getting it responsive on every device.",
              },
              {
                icon: ShoppingCart,
                h: "Shopify & WooCommerce",
                p: "Product setup, payments, shipping, checkout, and getting your store actually ready to sell.",
              },
              {
                icon: Palette,
                h: "Design & custom CSS",
                p: "Layout tweaks, typography, color, spacing, animations, and the custom CSS to make it look how you actually want.",
              },
              {
                icon: TrendingUp,
                h: "SEO & performance",
                p: "Speed and image optimization, caching, SEO basics, and getting Google Analytics and Search Console set up right.",
              },
              {
                icon: Mail,
                h: "Domains & hosting",
                p: "DNS, domain transfers, email setup, hosting migrations, and getting everything connected and talking to each other.",
              },
              {
                icon: Sparkles,
                h: "AI tools & automation",
                p: "Set up AI content workflows, a site assistant, or simple automations that take the busywork off your plate.",
              },
            ].map(({ icon: Icon, h, p }) => (
              <div
                key={h}
                className="group rounded-2xl border border-border bg-card p-7 transition hover:-translate-y-1 hover:border-brand-blue/30 hover:shadow-xl hover:shadow-brand-blue/10"
              >
                <div className="mb-4 flex items-center gap-3">
                  <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-brand-blue/15 to-pink-500/15">
                    <Icon className="h-5 w-5 text-brand-blue" strokeWidth={2.25} />
                  </div>
                  <h3 className="text-lg font-bold tracking-tight">{h}</h3>
                </div>
                <p className="text-[15px] text-muted-foreground">{p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHO IT'S FOR */}
      <section id="who" className="px-6 py-20">
        <div className="mx-auto max-w-6xl">
          <SectionHead
            maxWidth="max-w-3xl lg:max-w-none"
            titleClassName="lg:whitespace-nowrap"
            kickerWeight="font-bold"
            kicker="Who it's for"
            title={
              <>
                Built for people who'd rather <span className={gradText}>learn than wait.</span>
              </>
            }
          />
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {[
              {
                icon: Briefcase,
                h: "Small business owners",
                p: "Tired of waiting on a developer for every little change. Learn to update content, add pages, and run your own site.",
              },
              {
                icon: Users,
                h: "DIY website builders",
                p: "You started building and hit a wall. A layout that won't cooperate, a plugin that won't work, a design that looks off.",
              },
              {
                icon: MessageSquare,
                h: "Freelancers & agencies",
                p: "Need a second set of expert eyes on a client project? We'll troubleshoot, review your approach, or teach you a technique.",
              },
              {
                icon: GraduationCap,
                h: "People learning web design",
                p: "Faster than any course. You learn by doing on a real project with a pro guiding each step, not watching someone else code.",
              },
              {
                icon: Store,
                h: "Online store owners",
                p: "WooCommerce or Shopify giving you headaches? We'll fix product setup, payments, shipping, and checkout until it runs smooth.",
              },
              {
                icon: PenTool,
                h: "Creators & bloggers",
                p: "Your content deserves a site that looks as good as it reads. We'll fix your layout, categories, and SEO basics.",
              },
            ].map(({ icon: Icon, h, p }) => (
              <div
                key={h}
                className="rounded-2xl border border-border bg-card p-7 text-center transition hover:-translate-y-1 hover:shadow-xl hover:shadow-brand-blue/10"
              >
                <div className="mx-auto mb-4 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-brand-blue/15 to-pink-500/15">
                  <Icon className="h-5 w-5 text-brand-blue" strokeWidth={2.25} />
                </div>
                <h3 className="text-lg font-bold tracking-tight">{h}</h3>
                <p className="mt-2 text-[15px] text-muted-foreground">{p}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT */}
      <AboutSection
        className="pt-2 pb-6 sm:pt-4 sm:pb-8"
        contentClassName="mt-2 sm:mt-4"
        hideMockupButton
        meetingButtonPrimary
      />

      {/* PRICING */}
      <section id="pricing" className="px-6 py-20">
        <div className="mx-auto max-w-6xl">
          <SectionHead
            maxWidth="max-w-3xl"
            kickerWeight="font-bold"
            kicker="Pricing"
            title={
              <>
                One flat rate. <span className={gradText}>No surprises.</span>
              </>
            }
          >
            Two hours of live, hands-on help plus 15 days of text support. That's it.
          </SectionHead>
          <div className="mx-auto max-w-md">
            <div className="relative flex flex-col rounded-2xl border border-transparent bg-card p-8 shadow-2xl shadow-purple-500/20 ring-2 ring-transparent [background:linear-gradient(var(--card),var(--card))_padding-box,linear-gradient(100deg,oklch(0.585_0.196_263),#a855f7,#ec4899)_border-box]">
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-gradient-to-r from-brand-blue via-purple-500 to-pink-500 px-4 py-1.5 text-[11px] font-extrabold uppercase tracking-wider text-white">
                1-on-1 Session
              </span>
              <h3 className="text-lg font-bold tracking-tight">Website Help Session</h3>
              <div className="mt-1 text-sm text-muted-foreground">
                Live screen-share with a senior creative director
              </div>
              <div className="mt-5 text-5xl font-extrabold tracking-tight">
                $300
                <small className="ml-1 text-sm font-semibold text-muted-foreground">/session</small>
              </div>
              <div className="mt-1 text-xs font-medium text-muted-foreground">
                One-time payment, no subscriptions
              </div>
              <ul className="mt-6 flex-1 space-y-3">
                {[
                  "2 hours of live screen-sharing",
                  "Hands-on help on your actual site",
                  "Short prep questionnaire",
                  "15 days of text support after",
                  "No contracts, no subscriptions",
                ].map((li) => (
                  <li key={li} className="flex items-start gap-2 text-sm font-medium">
                    <Check
                      className="mt-0.5 h-4 w-4 flex-shrink-0 text-brand-green"
                      strokeWidth={3}
                    />
                    {li}
                  </li>
                ))}
              </ul>
              <a
                href="#contact"
                onClick={(e) => {
                  e.preventDefault();
                  scrollToAnchor("contact");
                }}
                className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-brand-blue via-purple-500 to-pink-500 px-5 py-3 text-sm font-bold text-white shadow-lg shadow-purple-500/40 transition hover:-translate-y-0.5 hover:opacity-90"
              >
                Get started <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="bg-muted/30 px-6 py-20">
        <div className="mx-auto max-w-5xl">
          <SectionHead
            maxWidth="max-w-3xl"
            kickerWeight="font-bold"
            kicker="Questions"
            title={
              <>
                Before you <span className={gradText}>book.</span>
              </>
            }
          />
          {(() => {
            const items: [string, string][] = [
              [
                "What if we don't finish in two hours?",
                "Most things get sorted inside the session, and the 15 days of text support catches the rest. If you've got a bigger project, we'll tell you honestly and point you to the right next step.",
              ],
              [
                "Do I need to prep anything?",
                "Just a short questionnaire we send after you book, so we know your site and your goal before the call starts. That way no time gets wasted on setup.",
              ],
              [
                "What platforms and tools do you cover?",
                "WordPress, Elementor, Shopify, WooCommerce, custom CSS, SEO basics, hosting and domains, and modern AI tools. If you're not sure we cover it, ask before booking.",
              ],
              [
                "Can you just do it for me instead?",
                "The session is hands-on, so we build right alongside you. If you'd rather hand the whole thing off, we also do full website projects. We'll point you that way if it's the better fit.",
              ],
              [
                "What happens after the call?",
                "You get 15 days of text support to ask quick follow-ups on anything we covered, so you're not left stranded the moment the call ends.",
              ],
              [
                "What if it's not helpful?",
                "We're confident you'll walk away with real answers. If the session genuinely didn't help, reach out and we'll make it right.",
              ],
            ];
            const col1 = items.filter((_, i) => i % 2 === 0);
            const col2 = items.filter((_, i) => i % 2 === 1);
            return (
              <div className="mx-auto mt-12 grid max-w-6xl gap-x-12 gap-y-2 md:grid-cols-2">
                <FaqColumn items={col1} />
                <FaqColumn items={col2} />
              </div>
            );
          })()}
        </div>
      </section>

      <section id="contact">
        <ContactSection
          compact
          heading={
            <h2 className="text-5xl font-bold leading-[1.05] tracking-tight text-foreground sm:text-6xl">
              Get unstuck
              <br />
              <span className={gradText}>this week.</span>
            </h2>
          }
          subheading={
            <p className="mt-6 text-lg text-muted-foreground">
              Book a 1-on-1 session and walk away knowing exactly what to do with your website. The
              only thing you're risking is 30 min.
            </p>
          }
          ctaText="Book my session"
        />
      </section>

      <Footer
        logo={<img src={logo} alt="Mind Media" className="h-7 w-auto" />}
        brandName="Mind Media"
        socialLinks={FOOTER_SOCIAL_LINKS}
        mainLinks={[
          { href: "/", label: "Home" },
          { href: "/seo", label: "SEO" },
          { href: "/ai-services", label: "AI Services" },
          { href: "/1-on-1-website-help", label: "1-on-1 Help" },
          { href: "/#pricing", label: "Pricing" },
          { href: "#faq", label: "FAQ" },
          { href: "/blog", label: "Blog" },
          { href: "/services", label: "Services" },
        ]}
        legalLinks={[
          { href: "/privacy", label: "Privacy" },
          { href: "/terms", label: "Terms" },
        ]}
        copyright={{
          text: `© ${new Date().getFullYear()} Mind Media`,
          license: "All rights reserved",
        }}
      />
    </main>
  );
}

function Kicker({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <span
      className={cn(
        "inline-block rounded-full bg-brand-blue/10 px-4 py-1.5 text-xs uppercase tracking-[0.12em] text-brand-blue",
        className,
      )}
    >
      {children}
    </span>
  );
}

function SectionHead({
  kicker,
  title,
  children,
  kickerWeight = "font-bold",
  maxWidth = "max-w-2xl",
  titleClassName,
}: {
  kicker: string;
  title: React.ReactNode;
  children?: React.ReactNode;
  kickerWeight?: "font-bold" | "font-medium";
  maxWidth?: string;
  titleClassName?: string;
}) {
  return (
    <div className={cn("mx-auto mb-12 text-center", maxWidth)}>
      <Kicker className={kickerWeight}>{kicker}</Kicker>
      <h2
        className={cn(
          "mt-4 text-4xl font-bold tracking-tight text-foreground sm:text-5xl lg:text-5xl",
          titleClassName,
        )}
      >
        {title}
      </h2>
      {children && <p className="mt-4 text-lg leading-relaxed text-muted-foreground">{children}</p>}
    </div>
  );
}

function FaqColumn({ items }: { items: [string, string][] }) {
  return (
    <Accordion type="single" collapsible className="w-full">
      {items.map(([q, a]) => (
        <AccordionItem key={q} value={q} className="border-b">
          <AccordionTrigger className="text-left text-base font-medium text-gray-950 md:text-lg">
            {q}
          </AccordionTrigger>
          <AccordionContent>
            <p className="text-muted-foreground text-base">{a}</p>
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}
